import './GalleryPopup.scss'
import { useLockBodyScroll, useWindowSize } from '@uidotdev/usehooks'
import { clsx } from 'clsx'
import type { MouseEvent, ReactNode } from 'react'
import { useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { DatoImage } from '#src/components/image/DatoImage.js'
import { LazyAnimation } from '#src/components/LazyAnimation.js'
import { VimeoPlayer } from '#src/components/VimeoPlayer.js'
import type { GalleryPopupQuery } from '#src/data/data.js'
import { useClickOutside } from '#src/hooks/useClickOutside.js'
import { useKeyDown } from '#src/hooks/useKeyDown.js'
import { languageService } from '#src/languageService.js'

export interface GalleryPopupProps {
  data: GalleryPopupQuery | null
  backHref?: string
}

export function GalleryPopup(props: GalleryPopupProps): ReactNode {
  useLockBodyScroll()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const containerRef = useRef<HTMLDivElement>(null)
  const windowSize = useWindowSize()
  const width = windowSize.width || 0
  const isMobile = width <= 600 // todo: implement in css media query instead
  // props.backHref is a canonical path (e.g. "/"), so localize it to the active
  // language; the pathname-derived fallback is already localized.
  const backHref = props.backHref
    ? languageService.localizePath(props.backHref, languageService.languageFromPath(pathname))
    : pathname.split('/').slice(0, -1).join('/')

  function goBack(): void {
    // alert('length: ' + globalThis.history.length)
    if ((globalThis.history?.length || 0) > 2) {
      void navigate(-1)
    } else {
      void navigate(backHref || '/', { replace: true })
    }
  }

  function closeClick(e: MouseEvent): void {
    e.preventDefault()
    // now it propagates to the onClickOutside handler, let it be
    // e.stopPropagation()
    // goBack()
  }

  useKeyDown('Escape', goBack)
  useClickOutside(containerRef, goBack)

  const data = props.data
  if (!data) {
    return <div className="gallery">Gallery not found</div>
  }

  const { imageGallery, videoGallery } = data
  const title = imageGallery?.title || videoGallery?.title
  const subtitle = imageGallery?.subtitle || videoGallery?.subtitle

  // const _numberOfVerticalImagesIsOdd =
  //   !!imageGallery &&
  //   imageGallery.images.filter(img => !img.tags?.includes('horizontal')).length % 2 === 1

  return (
    <div className="gallery">
      <Link to={backHref} className="gallery__cross" onClick={closeClick}>
        x
      </Link>

      <div className="gallery__text">
        <h1 className="gallery__title">{title}</h1>
        {subtitle && <p className="gallery__subtitle">{subtitle}</p>}
      </div>

      <div className="gallery__inner-container" ref={containerRef}>
        <div className="gallery__images">
          {imageGallery?.images.map((img, i) => (
            <LazyAnimation
              className={clsx({
                'gallery__image-container': true,
                'gallery__image-container_horizontal': img.tags?.includes('horizontal'),
                'gallery__image-container_single':
                  !img.tags?.includes('horizontal') && !isMobile && img.tags?.includes('single'),
              })}
              key={img.url}
            >
              <DatoImage
                data={img.responsiveImage}
                fadeInDuration={300}
                intersectionMargin="150px 0px 0px 0px"
                priority={i < 2} // top 2 images load with high prio, others to load lazily
                containerSize={img.tags?.includes('horizontal') ? 100 : 50}
                className={clsx({
                  gallery__image: true,
                  gallery__image_horizontal: img.tags?.includes('horizontal'),
                })}
                style={{ maxWidth: '100%' }}
              />
            </LazyAnimation>
          ))}
          {videoGallery && (
            <LazyAnimation
              className={clsx({
                'gallery__image-container': true,
                'gallery__image-container_horizontal': videoGallery.horizontal,
                'gallery__image-container_single': !videoGallery.horizontal && !isMobile,
              })}
            >
              <VimeoPlayer
                video={videoGallery.video}
                controls={true}
                className={clsx({
                  gallery__image: true,
                })}
              />
            </LazyAnimation>
          )}
        </div>
      </div>

      <Link to={backHref} className="gallery__cross" onClick={closeClick}>
        x
      </Link>
    </div>
  )
}
